// ── the archive: everything innernet holds for a person, as files ────────────
//
// One press out (settings → data, GET /v1/export, innernet_export): a folder of
// markdown and json that any tool can open, with nothing in it that needs
// innernet to be read. Trust in a memory is only rational with an exit, and
// this is the exit.
//
// This module only RENDERS: rows in, files out. No database, no keys, no
// network, so the shape is tested on its own. lib/export/gather reads the rows
// (and decides what may leave), lib/export/zip packs the files.
//
// Two laws:
//   • it goes back in through the doors that already exist. Each
//     projects/<slug>/project.json is a POST /v1/projects body (every page and
//     every cell), and you/facts/ is a folder `innernet import dir` reads: every
//     file under the cli's 4,000-character cap, the person's own words between
//     <user_said> marks so netti keeps them as theirs.
//   • it writes only what it is given. Whatever the private ring held back,
//     and every room that is not the person's, never reaches this module.

import { renderInjectCloud, type CommitRow, type DimensionRow, type NodeRow, type ProjectRow } from '../inject'
import { defangPersonTags, personBlock } from '../memory/voices'

export const ARCHIVE_FORMAT = 'innernet-archive'
export const ARCHIVE_VERSION = 1

/** `innernet import dir` (apps/cli importSources) clips a file past 4,000
 *  characters and reads at most 80 files; a fact file stays under both. */
export const FACT_FILE_CHARS = 3_800
export const IMPORT_DIR_MAX_FILES = 80
/** past these, a statement or a quote is clipped in the markdown (facts.json
 *  keeps it whole), so one fact always fits a file with room to spare */
const STATEMENT_CHARS = 800
const QUOTE_CHARS = 1_000
const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n).trimEnd()}…` : s)

type Row = Record<string, unknown>

export interface ExportDimension {
  name: string
  /** null = a sealed page that did not open; it is listed, never written */
  content: string | null
  lines?: number | null
  updated_at?: string | null
}

export interface ExportNode {
  node_id: string
  type: string
  content: Record<string, unknown> | null
}

export interface ExportCommit {
  hash: string
  parent_hash: string | null
  committed_at: string
  summary: string
  description?: string | null
  trigger?: string | null
  client?: string | null
  diff?: unknown
}

export interface ExportBranch {
  name: string
  status: string | null
  fork_from: string | null
  summary: string | null
  created_at: string | null
  resolved_at: string | null
  /** the branch's own working copy (never the trunk snapshot it forked from) */
  dimensions: Record<string, unknown>
  nodes: Record<string, unknown>
}

export interface ExportArtifact {
  artifact_id: string
  title: string
  status: string
  document: string
  summary: string | null
  fragment_count: number
  created_at: string | null
  updated_at: string | null
  /** findings that have not been folded into the document yet */
  pending: Array<{ content: string; section_hint: string | null; created_at: string | null }>
}

export interface ExportProject {
  slug: string
  name: string
  type: string
  tagline: string | null
  schema_version: string
  manifest: Record<string, unknown>
  capture_protocol: string | null
  created_at: string | null
  updated_at: string | null
  parked: boolean
  dimensions: ExportDimension[]
  nodes: ExportNode[]
  /** oldest first */
  commits: ExportCommit[]
  branches: ExportBranch[]
  /** the live handoff per stream */
  handoffs: Array<{ scope: string; document: string; updated_at: string | null }>
  /** every handoff ever written, oldest first */
  handoffHistory: Array<{ scope: string; document: string; created_at: string | null }>
  tasks: Row[]
  artifacts: ExportArtifact[]
  /** cells whose ciphertext did not open */
  unreadableCells: number
}

export type FactVoiceValue = 'said' | 'reported' | 'inferred' | null

export interface ExportFactSource {
  quote: string | null
  /** whose words the quote is: said (the person's) · summary · inferred */
  kind: string
  source: string | null
  client: string | null
  observed_at: string | null
}

export interface ExportFact {
  id: string
  dimension: string
  /** null = sealed and did not open */
  statement: string | null
  disclosure: string
  voice: FactVoiceValue
  confidence: number | null
  /** false = an earlier version, closed when something newer replaced it */
  current: boolean
  valid_from: string | null
  valid_to: string | null
  supersedes: string | null
  tags: string[]
  created_at: string | null
  sources: ExportFactSource[]
}

export interface ExportAccount {
  id: string
  email: string | null
  created_at: string | null
  api_keys: Row[]
  connections: Row[]
}

export interface ExportData {
  exportedAt: string
  /** one map, or everything the person holds */
  scope: { kind: 'everything' } | { kind: 'project'; slug: string }
  /** the person's own session only: who they are, their keys and the tools they let in */
  account?: ExportAccount | null
  projects: ExportProject[]
  /** null when the scope is one map */
  facts: ExportFact[] | null
  questions: Row[]
  /** what the person told rooms: their own ledger and their own words, never anyone else's */
  rooms: { ledger: Row[]; captures: Row[] } | null
  /** tasks tied to no map (older notion mirrors) */
  looseTasks: Row[]
  /** what the private ring held back from the caller: a count, never a word */
  withheld?: { count: number; classes: string[]; note: string } | null
  /** facts whose ciphertext did not open */
  unreadableFacts?: number
}

export interface ArchiveFile {
  /** relative to the archive root, forward slashes */
  path: string
  content: string
  /** ids of the personal facts whose words this file carries (for the reveal ledger) */
  facts?: string[]
}

export interface ArchiveProjectEntry {
  slug: string
  name: string
  path: string
  pages: number
  cells: number
  commits: number
  tasks: number
  artifacts: number
  handoffs: number
  branches: number
}

export interface ArchiveManifest {
  format: typeof ARCHIVE_FORMAT
  version: number
  exported_at: string
  scope: 'everything' | 'project'
  counts: {
    projects: number
    pages: number
    cells: number
    commits: number
    facts: number
    earlier_versions: number
    sources: number
    handoffs: number
    tasks: number
    artifacts: number
  }
  projects: ArchiveProjectEntry[]
  withheld?: { count: number; classes: string[]; note: string }
  unreadable?: { pages: string[]; cells: number; facts: number }
  files: number
}

export interface Archive {
  /** the folder name everything sits under (and the zip's name) */
  root: string
  files: ArchiveFile[]
  manifest: ArchiveManifest
}

// ── paths ────────────────────────────────────────────────────────────────────

// control characters, and the characters windows will not put in a file name
// eslint-disable-next-line no-control-regex
const UNSAFE = /[\u0000-\u001f\u007f<>:"\\|?*]/g

/** One path segment a file system will take: no traversal, no hidden dot, no reserved name. */
export function safeSegment(raw: string): string {
  let s = String(raw ?? '').replace(UNSAFE, '-').replace(/\s+/g, ' ').trim()
  // no leading dot (a hidden file, or `..`), no trailing dot or space (windows drops them)
  s = s.replace(/^[.\s]+/, '').replace(/[.\s]+$/, '')
  if (s.length > 100) s = s.slice(0, 100).replace(/[.\s]+$/, '')
  if (!s) s = '_'
  if (/^(con|prn|aux|nul|com\d|lpt\d)$/i.test(s)) s = `_${s}`
  return s
}

/** A page name to a relative path: "/" makes folders, every segment made safe. */
export function safePath(name: string): string {
  const parts = String(name ?? '').split('/').map((p) => p.trim())
  const kept = parts.filter((p, i) => p !== '' || (i > 0 && i < parts.length - 1))
  return (kept.length ? kept : ['_']).map(safeSegment).join('/')
}

/** Hands out paths that never collide, case-insensitively (macOS and windows fold case). */
function pathClaimer() {
  const taken = new Set<string>()
  return (path: string): string => {
    const dot = path.lastIndexOf('.')
    const slash = path.lastIndexOf('/')
    const [stem, ext] = dot > slash ? [path.slice(0, dot), path.slice(dot)] : [path, '']
    let candidate = path
    for (let n = 2; taken.has(candidate.toLowerCase()); n++) candidate = `${stem}-${n}${ext}`
    taken.add(candidate.toLowerCase())
    return candidate
  }
}

const json = (v: unknown) => JSON.stringify(v, null, 2) + '\n'
const day = (iso: string | null | undefined) => (iso ? String(iso).slice(0, 10) : '')

// ── personal memory ──────────────────────────────────────────────────────────

const VOICE_WORDS: Record<string, string> = {
  said: 'in your words',
  reported: "an ai's note",
  inferred: "netti's reading",
}

/** The person's own words for a fact: its quotes marked said, oldest first, at most two. */
function saidQuotes(f: ExportFact): string[] {
  const out: string[] = []
  for (const s of f.sources) {
    if (s.kind !== 'said' || !s.quote?.trim()) continue
    const q = s.quote.trim()
    if (out.includes(q)) continue
    out.push(clip(q, QUOTE_CHARS))
    if (out.length === 2) break
  }
  return out
}

function factEntry(f: ExportFact): string {
  // the statement is innernet's phrasing, never the person's words: any person
  // mark inside it is shown, not obeyed (only the quotes below carry one)
  const statement = clip(defangPersonTags(String(f.statement ?? '')).replace(/\s+/g, ' ').trim(), STATEMENT_CHARS)
  const meta = [f.disclosure, f.voice ? VOICE_WORDS[f.voice] : 'unmarked'].join(' · ')
  const lines = [`- ${statement}`, `  ${meta}`]
  for (const q of saidQuotes(f)) lines.push('', personBlock(q))
  return lines.join('\n')
}

/** The facts as they stand, one area per file, each file small enough for `innernet import dir`. */
function factFiles(facts: ExportFact[], exportedAt: string, claim: (p: string) => string): ArchiveFile[] {
  const byDimension = new Map<string, ExportFact[]>()
  for (const f of facts) {
    if (!f.current || f.statement == null || !f.statement.trim()) continue
    const list = byDimension.get(f.dimension)
    if (list) list.push(f)
    else byDimension.set(f.dimension, [f])
  }
  const files: ArchiveFile[] = []
  for (const dimension of [...byDimension.keys()].sort((a, b) => a.localeCompare(b))) {
    const list = byDimension.get(dimension)!.sort(
      (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0) || String(a.statement).localeCompare(String(b.statement)),
    )
    const header = (part: number) =>
      `# ${dimension}${part > 1 ? ` (${part})` : ''}\n\n` +
      `your personal memory under "${dimension}", as it stood on ${day(exportedAt)}. each fact says who may read it and whose words it rests on.\n`
    let part = 1
    let body = header(part)
    let ids: string[] = []
    const flush = () => {
      const base = `you/facts/${safeSegment(dimension)}${part > 1 ? `-${part}` : ''}.md`
      files.push({ path: claim(base), content: body.trimEnd() + '\n', facts: ids })
    }
    for (const f of list) {
      const entry = `\n${factEntry(f)}\n`
      if (ids.length && body.length + entry.length > FACT_FILE_CHARS) {
        flush()
        part += 1
        body = header(part)
        ids = []
      }
      body += entry
      ids.push(f.id)
    }
    flush()
  }
  return files
}

/** Every fact, now and before: who said it, who may read it, every telling, what it replaced. */
function factsJson(facts: ExportFact[]): string {
  const replacedBy = new Map<string, string>()
  for (const f of facts) if (f.supersedes) replacedBy.set(f.supersedes, f.id)
  const ordered = [...facts].sort(
    (a, b) =>
      Number(b.current) - Number(a.current) ||
      a.dimension.localeCompare(b.dimension) ||
      String(a.valid_from ?? '').localeCompare(String(b.valid_from ?? '')),
  )
  return json({
    about:
      'every personal fact innernet held for you. current: false marks an earlier version, closed when a newer one replaced it (replaced_by). ' +
      'voice is whose words a fact rests on: said (yours), reported (an ai\'s note), inferred (netti\'s reading). ' +
      'disclosure is who may read it: private, self, trusted, work, public. sources are each time it was told, with the words quoted.',
    facts: ordered.map((f) => ({
      id: f.id,
      dimension: f.dimension,
      statement: f.statement,
      disclosure: f.disclosure,
      voice: f.voice,
      confidence: f.confidence,
      current: f.current,
      valid_from: f.valid_from,
      valid_to: f.valid_to,
      supersedes: f.supersedes,
      replaced_by: replacedBy.get(f.id) ?? null,
      tags: f.tags,
      created_at: f.created_at,
      sources: f.sources,
    })),
  })
}

// ── one map ──────────────────────────────────────────────────────────────────

function projectFiles(p: ExportProject, dir: string, exportedAt: string): { files: ArchiveFile[]; unreadablePages: string[] } {
  const files: ArchiveFile[] = []
  const claim = pathClaimer()
  const put = (path: string, content: string) => files.push({ path: `${dir}/${claim(path)}`, content })

  const readable = p.dimensions.filter((d): d is ExportDimension & { content: string } => typeof d.content === 'string')
  const unreadablePages = p.dimensions.filter((d) => typeof d.content !== 'string').map((d) => `${p.slug}/${d.name}`)
  const cells = p.nodes.filter((n) => n.content != null)

  // the render a coding agent reads (lib/inject — the same one `innernet` injects)
  const row: ProjectRow = {
    slug: p.slug,
    name: p.name,
    type: p.type,
    tagline: p.tagline,
    schema_version: p.schema_version,
    manifest: p.manifest,
  }
  const dims: DimensionRow[] = readable.map((d) => ({
    name: d.name,
    content: d.content,
    lines: typeof d.lines === 'number' ? d.lines : d.content.split('\n').length,
  }))
  const nodes: NodeRow[] = cells.map((n) => ({ node_id: n.node_id, type: n.type, content: n.content ?? {} }))
  const newestFirst: CommitRow[] = [...p.commits].reverse().map((c) => ({
    hash: c.hash,
    parent_hash: c.parent_hash,
    summary: c.summary,
    trigger: c.trigger ?? null,
    committed_at: c.committed_at,
  }))
  put('AGENTS.md', renderInjectCloud('claude', row, dims, nodes, newestFirst))
  put('CLAUDE.md', '@AGENTS.md\n')

  // the map, ready to go back in: POST /v1/projects takes this body as it is
  put(
    'project.json',
    json({
      name: p.name,
      slug: p.slug,
      type: p.type,
      tagline: p.tagline,
      commit_message: `brought back from an innernet archive of ${day(exportedAt)}`,
      dimensions: Object.fromEntries(readable.map((d) => [d.name, d.content])),
      nodes: Object.fromEntries(cells.map((n) => [n.node_id, { type: n.type, content: n.content }])),
    }),
  )
  put(
    'meta.json',
    json({
      slug: p.slug,
      name: p.name,
      type: p.type,
      tagline: p.tagline,
      schema_version: p.schema_version,
      created_at: p.created_at,
      updated_at: p.updated_at,
      parked: p.parked,
      capture_protocol: p.capture_protocol,
      manifest: p.manifest,
    }),
  )

  for (const d of readable) put(`dimensions/${safePath(d.name)}.md`, d.content.endsWith('\n') ? d.content : `${d.content}\n`)
  if (cells.length) put('nodes.json', json(cells.map((n) => ({ node_id: n.node_id, type: n.type, content: n.content }))))
  if (p.commits.length) {
    put(
      'commits.jsonl',
      p.commits
        .map((c) =>
          JSON.stringify({
            hash: c.hash,
            parent_hash: c.parent_hash,
            committed_at: c.committed_at,
            summary: c.summary,
            description: c.description ?? null,
            trigger: c.trigger ?? null,
            client: c.client ?? null,
            diff: c.diff ?? null,
          }),
        )
        .join('\n') + '\n',
    )
  }
  if (p.branches.length) put('branches.json', json(p.branches))

  for (const h of [...p.handoffs].sort((a, b) => a.scope.localeCompare(b.scope))) {
    if (!h.document.trim()) continue
    put(h.scope === 'project' ? 'handoff.md' : `handoffs/${safeSegment(h.scope)}.md`, h.document.endsWith('\n') ? h.document : `${h.document}\n`)
  }
  if (p.handoffHistory.length) put('handoffs.json', json(p.handoffHistory))
  if (p.tasks.length) put('tasks.json', json(p.tasks))

  if (p.artifacts.length) {
    for (const a of p.artifacts) {
      if (a.document.trim()) put(`artifacts/${safeSegment(a.artifact_id)}.md`, a.document.endsWith('\n') ? a.document : `${a.document}\n`)
    }
    put('artifacts.json', json(p.artifacts.map(({ document: _doc, ...rest }) => rest)))
  }
  return { files, unreadablePages }
}

// ── the guide ────────────────────────────────────────────────────────────────

function readme(data: ExportData, m: ArchiveManifest, factFileCount: number): string {
  const everything = data.scope.kind === 'everything'
  const lines: string[] = []
  lines.push(everything ? '# your innernet' : `# ${m.projects[0]?.name ?? 'one memory'}, from your innernet`)
  lines.push('')
  lines.push(
    everything
      ? `everything innernet held for you on ${day(m.exported_at)}, as files any tool can open: markdown for reading, json for machines. nothing in here needs innernet to be read.`
      : `one memory as innernet held it on ${day(m.exported_at)}, as files any tool can open: markdown for reading, json for machines.`,
  )
  lines.push('')
  if (everything && data.facts) {
    lines.push('this folder holds your personal memory in plain text, the private parts too. please keep it somewhere only you can open.')
    lines.push('')
  }
  lines.push('## what is here', '')
  if (everything && data.facts) {
    lines.push('- `you/` your personal memory.')
    lines.push('  - `facts/` one file per area, the facts as they stand now. where a fact rests on your own words, those words sit between `<user_said>` marks.')
    lines.push("  - `facts.json` every fact, now and before: whose words it rests on (said, an ai's note, netti's reading), who may read it (private, self, trusted, work, public), each time it was told, and the version it replaced.")
    if (data.questions.length) lines.push('  - `questions.json` what netti asked you, and what it still wants to ask.')
  }
  lines.push('- `projects/<slug>/` one folder per memory you own.')
  lines.push('  - `AGENTS.md` the whole map, written for an ai tool to read. `CLAUDE.md` points to it, the way claude code expects.')
  lines.push('  - `dimensions/` every page, as markdown.')
  lines.push('  - `nodes.json` the cells of the map.')
  lines.push('  - `commits.jsonl` every change, oldest first, one per line.')
  lines.push('  - `handoff.md` where the last session left off. `handoffs.json` every handoff ever written.')
  lines.push('  - `tasks.json` the work, open and done.')
  lines.push('  - `artifacts/` each living document. `artifacts.json` their state, with any findings not yet folded in.')
  lines.push('  - `branches.json` the branches, and what each one changed.')
  lines.push('  - `project.json` the map, ready to go back in. `meta.json` how it was set up.')
  if (data.rooms) {
    lines.push("- `rooms/added.json` what you told rooms: your own record of what you added, and the words of your own captures. a room's memory belongs to the room, so nothing else from one is here.")
  }
  if (data.looseTasks.length) lines.push('- `tasks.json` tasks that belong to no map.')
  if (data.account) lines.push('- `account.json` your account: when it began, your keys and the tools you let in (names and dates, never a secret).')
  if (everything) lines.push('- each agent you keep has an export of its own, at https://innernet.live/api/v1/agents/<agent>/export.')
  lines.push('- `manifest.json` what this archive holds, counted.')
  lines.push('')

  if (m.withheld) {
    lines.push('## what was held back', '')
    lines.push(`${m.withheld.note} the export from settings → data at https://innernet.live/dashboard/settings always holds everything.`)
    lines.push('')
  }
  if (m.unreadable && (m.unreadable.pages.length || m.unreadable.cells || m.unreadable.facts)) {
    lines.push('## what would not open', '')
    lines.push('a few things are kept sealed, and the key innernet holds did not fit them. they are counted in `manifest.json` rather than left out quietly. please write to hello@innernet.live and we will look.')
    lines.push('')
  }

  lines.push('## bringing it back', '')
  lines.push('the easiest way: open https://innernet.live/dashboard/memory?v=import, press import your memory, choose in a file, and drop this zip as it is. it says what it holds before anything is sent.')
  lines.push('')
  lines.push('by hand, each map goes back in through the api, as it is. with a key from https://innernet.live/developers:')
  lines.push('')
  lines.push('```bash')
  lines.push('curl -X POST https://innernet.live/api/v1/projects \\')
  lines.push('  -H "Authorization: Bearer $INNERNET_KEY" -H "Content-Type: application/json" \\')
  lines.push('  --data @projects/<slug>/project.json')
  lines.push('```')
  lines.push('')
  lines.push('sent to an account that still holds a map of that name, it writes these pages over the ones there. every save is a commit, so the newer words stay in that map\'s history.')
  lines.push('')
  if (everything && data.facts) {
    lines.push('your personal memory goes back in from this folder:')
    lines.push('')
    lines.push('```bash')
    lines.push('npx innernet import dir you/facts')
    lines.push('```')
    lines.push('')
    lines.push('netti reads the facts again and files each one. what was in your own words comes back as yours.')
    if (factFileCount > IMPORT_DIR_MAX_FILES) {
      lines.push(
        `\`you/facts\` holds ${factFileCount} files and the import reads ${IMPORT_DIR_MAX_FILES} at a time, so please bring them back in parts: move ${IMPORT_DIR_MAX_FILES} into a folder of their own, import it, then the next.`,
      )
    }
    lines.push('')
  }
  return lines.join('\n')
}

// ── the whole archive ────────────────────────────────────────────────────────

export function archiveRoot(data: Pick<ExportData, 'exportedAt' | 'scope'>): string {
  const d = day(data.exportedAt) || 'today'
  return data.scope.kind === 'project' ? `innernet-${safeSegment(data.scope.slug)}-${d}` : `innernet-${d}`
}

export function renderArchive(data: ExportData): Archive {
  const files: ArchiveFile[] = []
  const projectEntries: ArchiveProjectEntry[] = []
  const unreadablePages: string[] = []
  let unreadableCells = 0

  const claimProjectDir = pathClaimer()
  for (const p of [...data.projects].sort((a, b) => a.slug.localeCompare(b.slug))) {
    const dir = claimProjectDir(`projects/${safeSegment(p.slug)}`)
    const out = projectFiles(p, dir, data.exportedAt)
    files.push(...out.files)
    unreadablePages.push(...out.unreadablePages)
    unreadableCells += p.unreadableCells
    projectEntries.push({
      slug: p.slug,
      name: p.name,
      path: `${dir}/`,
      pages: p.dimensions.length - out.unreadablePages.length,
      cells: p.nodes.filter((n) => n.content != null).length,
      commits: p.commits.length,
      tasks: p.tasks.length,
      artifacts: p.artifacts.length,
      handoffs: p.handoffs.filter((h) => h.document.trim()).length,
      branches: p.branches.length,
    })
  }

  const facts = data.facts ?? []
  let factFileCount = 0
  if (data.facts) {
    const claimYou = pathClaimer()
    const fileList = factFiles(facts, data.exportedAt, claimYou)
    factFileCount = fileList.length
    files.push(...fileList)
    if (facts.length) {
      files.push({ path: 'you/facts.json', content: factsJson(facts), facts: facts.filter((f) => f.statement != null).map((f) => f.id) })
    }
    if (data.questions.length) files.push({ path: 'you/questions.json', content: json(data.questions) })
  }
  if (data.rooms && (data.rooms.ledger.length || data.rooms.captures.length)) {
    files.push({
      path: 'rooms/added.json',
      content: json({
        about:
          "what you told rooms. contributions is your own record: what you added and, for anything a room kept back, only the reason. captures are the words of your own captures on a room's map. a room's memory belongs to the room, so nothing else from one is here.",
        contributions: data.rooms.ledger,
        captures: data.rooms.captures,
      }),
    })
  }
  if (data.looseTasks.length) files.push({ path: 'tasks.json', content: json(data.looseTasks) })
  if (data.account) files.push({ path: 'account.json', content: json(data.account) })

  const sum = (pick: (p: ArchiveProjectEntry) => number) => projectEntries.reduce((n, p) => n + pick(p), 0)
  const manifest: ArchiveManifest = {
    format: ARCHIVE_FORMAT,
    version: ARCHIVE_VERSION,
    exported_at: data.exportedAt,
    scope: data.scope.kind,
    counts: {
      projects: projectEntries.length,
      pages: sum((p) => p.pages),
      cells: sum((p) => p.cells),
      commits: sum((p) => p.commits),
      facts: facts.filter((f) => f.current).length,
      earlier_versions: facts.filter((f) => !f.current).length,
      sources: facts.reduce((n, f) => n + f.sources.length, 0),
      handoffs: sum((p) => p.handoffs),
      tasks: sum((p) => p.tasks) + data.looseTasks.length,
      artifacts: sum((p) => p.artifacts),
    },
    projects: projectEntries,
    ...(data.withheld && data.withheld.count > 0 ? { withheld: data.withheld } : {}),
    ...(unreadablePages.length || unreadableCells || data.unreadableFacts
      ? { unreadable: { pages: unreadablePages, cells: unreadableCells, facts: data.unreadableFacts ?? 0 } }
      : {}),
    files: files.length + 2,
  }

  files.unshift({ path: 'manifest.json', content: json(manifest) })
  files.unshift({ path: 'README.md', content: readme(data, manifest, factFileCount) })
  return { root: archiveRoot(data), files, manifest }
}

/** UTF-8 size of a file, as the zip will hold it. */
export function byteLength(text: string): number {
  return new TextEncoder().encode(text).length
}
